import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Receipt, Plus, X } from "lucide-react";
import { invoicesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import UpgradeBanner from "@/components/UpgradeBanner";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Invoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilterId = searchParams.get("client") || "";
  const clientFilterName = searchParams.get("name") || "";
  const { data, loading, error, reload } = useAsync(() => invoicesApi.list(), []);
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const all = Array.isArray(data) ? data : [];
    return clientFilterId ? all.filter((x) => x.client_id === clientFilterId) : all;
  }, [data, clientFilterId]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (x) =>
        (x.invoice_number || "").toLowerCase().includes(s) ||
        (x.building_name || "").toLowerCase().includes(s) ||
        (x.client_name || "").toLowerCase().includes(s)
    );
  }, [list, q]);

  const clearClientFilter = () => setSearchParams({}, { replace: true });

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Billing across your maintenance and inspection work" testId="invoices-header"
        actions={<Button onClick={() => navigate("/invoices/new")} data-testid="new-invoice-btn"><Plus className="mr-1.5 h-4 w-4" /> New invoice</Button>} />
      <UpgradeBanner feature="invoices" className="mb-4" title="Invoicing is a Pro feature">Raise, send and track invoices from your accepted quotes.</UpgradeBanner>

      {clientFilterId && (
        <div className="mb-4 flex items-center gap-2" data-testid="invoices-client-filter">
          <Badge variant="outline" className="gap-1.5 bg-primary/5 text-primary">
            Client: {clientFilterName || "selected"}
            <button onClick={clearClientFilter} className="ml-0.5 rounded-full hover:bg-primary/10" data-testid="invoices-client-filter-clear" aria-label="Clear client filter">
              <X className="h-3.5 w-3.5" />
            </button>
          </Badge>
        </div>
      )}

      {loading && <LoadingState label="Loading invoices…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="relative mb-4 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search invoices…" className="pl-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoices-search" />
          </div>

          {filtered.length === 0 ? (
            <EmptyState title={list.length === 0 ? "No invoices yet" : "No matches"} message={list.length === 0 ? "Invoices raised from accepted quotes appear here." : "Try a different search."} testId="invoices-empty" />
          ) : (
            <div className="space-y-2" data-testid="invoices-list">
              {filtered.map((x) => (
                <Card key={x.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md" onClick={() => navigate(`/invoices/${x.id}`)} data-testid={`invoice-row-${x.id}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                      <Receipt className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{x.invoice_number}</p>
                      <p className="truncate text-sm text-slate-500">
                        {x.building_name || x.client_name || "—"} · Due {fmtDate(x.due_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-slate-800">{fmtMoney(x.total)}</span>
                    <Badge variant="outline" className={statusTone(x.overdue ? "overdue" : x.status)}>
                      {x.overdue ? `Overdue ${x.days_overdue}d` : prettyStatus(x.status)}
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
